import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Home from './pages/Home';
import Signup from './pages/Signup';
import Login from './pages/Login';
import LoginTest from './pages/LoginTest';
import ClubFitting from './pages/ClubFitting';
import ClubFittingMobile from './pages/ClubFittingMobile';
import ClubFittingQuiz from './pages/ClubFittingQuiz';
import BasicFitting from './pages/BasicFitting';
import AdvancedFitting from './pages/AdvancedFitting';
import TopBar from './components/TopBar';
import './App.css';
import LearnMore from './pages/LearnMore';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ActivateAccount from './pages/ActivateAccount';
import TermsOfUse from './pages/TermsOfUse';
import PublicProfile from './pages/PublicProfile';

const PAGE_META: Record<string, { title: string; description: string; keywords: string }> = {
  '/': {
    title: 'NeutralFit | Online Golf Fitting & Club Recommendations',
    description: 'NeutralFit helps golfers find smarter golf fitting advice, club recommendations, and online golf fitting guidance based on swing tendencies, distances, and playing style.',
    keywords: 'golf fitting, online golf fitting, club recommendations, golf club fitting, golf equipment recommendations, custom golf clubs',
  },
  '/home': {
    title: 'NeutralFit | Online Golf Fitting & Club Recommendations',
    description: 'NeutralFit helps golfers find smarter golf fitting advice, club recommendations, and online golf fitting guidance based on swing tendencies, distances, and playing style.',
    keywords: 'golf fitting, online golf fitting, club recommendations, golf club fitting, golf equipment recommendations, custom golf clubs',
  },
  '/learn-more': {
    title: 'Learn More About NeutralFit Golf Fitting',
    description: 'Learn how NeutralFit uses swing data and personalized input to create better golf fitting recommendations for your driver, irons, wedges, and putter.',
    keywords: 'golf fitting explained, club recommendations, golf swing analysis, driver fitting, iron fitting, wedge fitting',
  },
  '/club-fitting': {
    title: 'Golf Club Fitting | NeutralFit',
    description: 'Get personalized golf club fitting guidance, compare new and used clubs, and receive data-driven club recommendations designed around your game.',
    keywords: 'golf club fitting, custom golf clubs, club recommendations, golf equipment fitting, driver fitting, irons fitting',
  },
  '/club-fitting-mobile': {
    title: 'Golf Club Fitting | NeutralFit',
    description: 'Use NeutralFit on mobile to get online golf fitting guidance, club recommendations, and bag suggestions tailored to your swing and golf goals.',
    keywords: 'mobile golf fitting, online golf fitting, club recommendations, golf fitting app, golf bag recommendations',
  },
  '/club-fitting-quiz': {
    title: 'Golf Club Fitting | NeutralFit',
    description: 'Use NeutralFit on mobile to get online golf fitting guidance, club recommendations, and bag suggestions tailored to your swing and golf goals.',
    keywords: 'mobile golf fitting, online golf fitting, club recommendations, golf fitting app, golf bag recommendations',
  },
  '/signup': {
    title: 'Create Your Free NeutralFit Account',
    description: 'Create a free NeutralFit account to save your golf profile and receive personalized golf fitting and club recommendations.',
    keywords: 'create neutralfit account, golf fitting account, golf profile, club recommendations sign up',
  },
  '/login': {
    title: 'Login to NeutralFit',
    description: 'Log in to NeutralFit to review your golf profile, saved recommendations, and online golf fitting progress.',
    keywords: 'neutralfit login, golf fitting login, club recommendations account, golf profile access',
  },
  '/terms': {
    title: 'Terms of Use | NeutralFit',
    description: 'Read the NeutralFit terms of use and website policies for golf fitting recommendations and equipment guidance.',
    keywords: 'neutralfit terms of use, golf fitting site terms, club recommendations policies',
  },
};

const updateMetaTags = (pathname: string) => {
  const meta = PAGE_META[pathname] || PAGE_META['/'];
  const canonicalUrl = `https://www.neutralfitgolf.com${pathname === '/' ? '' : pathname}`;

  document.title = meta.title;

  const descriptionTag = document.querySelector('meta[name="description"]') || document.createElement('meta');
  descriptionTag.setAttribute('name', 'description');
  descriptionTag.setAttribute('content', meta.description);
  if (!document.head.contains(descriptionTag)) document.head.appendChild(descriptionTag);

  const ogTitle = document.querySelector('meta[property="og:title"]') || document.createElement('meta');
  ogTitle.setAttribute('property', 'og:title');
  ogTitle.setAttribute('content', meta.title);
  if (!document.head.contains(ogTitle)) document.head.appendChild(ogTitle);

  const ogDescription = document.querySelector('meta[property="og:description"]') || document.createElement('meta');
  ogDescription.setAttribute('property', 'og:description');
  ogDescription.setAttribute('content', meta.description);
  if (!document.head.contains(ogDescription)) document.head.appendChild(ogDescription);

  const ogUrl = document.querySelector('meta[property="og:url"]') || document.createElement('meta');
  ogUrl.setAttribute('property', 'og:url');
  ogUrl.setAttribute('content', canonicalUrl);
  if (!document.head.contains(ogUrl)) document.head.appendChild(ogUrl);

  const canonical = document.querySelector('link[rel="canonical"]') || document.createElement('link');
  canonical.setAttribute('rel', 'canonical');
  canonical.setAttribute('href', canonicalUrl);
  if (!document.head.contains(canonical)) document.head.appendChild(canonical);

  const twitterTitle = document.querySelector('meta[name="twitter:title"]') || document.createElement('meta');
  twitterTitle.setAttribute('name', 'twitter:title');
  twitterTitle.setAttribute('content', meta.title);
  if (!document.head.contains(twitterTitle)) document.head.appendChild(twitterTitle);

  const twitterDescription = document.querySelector('meta[name="twitter:description"]') || document.createElement('meta');
  twitterDescription.setAttribute('name', 'twitter:description');
  twitterDescription.setAttribute('content', meta.description);
  if (!document.head.contains(twitterDescription)) document.head.appendChild(twitterDescription);

  const robots = document.querySelector('meta[name="robots"]') || document.createElement('meta');
  robots.setAttribute('name', 'robots');
  robots.setAttribute('content', 'index, follow, max-image-preview:large');
  if (!document.head.contains(robots)) document.head.appendChild(robots);
};

// Create a new component to be able to use hooks
const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    updateMetaTags(location.pathname);
  }, [location.pathname]);
  const handleSignOut = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    // Clear messenger conversation on sign out
    localStorage.removeItem('messenger-conversation');
    localStorage.removeItem('messenger-visible');
    // Clear club fitting results on sign out
    // localStorage.removeItem('club-fitting-results');
    navigate('/');
  };

  const isLoggedIn = Boolean(localStorage.getItem('token') || sessionStorage.getItem('token'));
  const showSharedTopBar =
    location.pathname !== '/club-fitting' &&
    location.pathname !== '/club-fitting-mobile' &&
    location.pathname !== '/club-fitting-quiz';

  return (
    <>
      {showSharedTopBar && <TopBar showSignOut={isLoggedIn} onSignOut={handleSignOut} />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/login" element={<Login />} />
        <Route path="/login-test" element={<LoginTest />} />
        <Route 
          path="/club-fitting" 
          element={<ClubFitting onSignOut={handleSignOut} />}
        />
        <Route 
          path="/club-fitting-mobile" 
          element={<ClubFittingMobile />}
        />
        <Route 
          path="/club-fitting-quiz" 
          element={<ClubFittingQuiz />}
        />
        <Route path="/basic-fitting" element={<BasicFitting />} />
        <Route path="/advanced-fitting" element={<AdvancedFitting />} />
        <Route path="/learn-more" element={<LearnMore />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/activate-account" element={<ActivateAccount />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/bag/:username" element={<PublicProfile />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
